import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  useTheme, 
  CircularProgress, 
  FormControl, 
  Select, 
  MenuItem, 
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer, 
  ReferenceLine,
  ReferenceArea,
  Cell
} from 'recharts';
import { useDomain, DOMAINS } from '../../contexts/DomainContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse } from 'date-fns';
import ko from "date-fns/locale/ko";
import DashboardTitle from '../Common/DashboardTitle';
import AssessmentIcon from '@mui/icons-material/Assessment';

const lightenHexColor = (hex, factor = 0.3) => {
  if (typeof hex !== 'string' || !/^#([0-9a-fA-F]{6})$/.test(hex)) {
    return hex;
  }
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.min(255, Math.round(r + (255 - r) * factor));
  g = Math.min(255, Math.round(g + (255 - g) * factor));
  b = Math.min(255, Math.round(b + (255 - b) * factor));

  const toHex = (value) => value.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * KPI 차트 컴포넌트
 * 
 * @param {Object} props - 컴포넌트 속성
 * @param {Object} props.kpiData - KPI 차트 데이터
 * @param {boolean} props.isLoading - 로딩 상태
 * @param {Function} props.onFilterChange - 필터 변경 핸들러
 * @param {Object} props.filter - 현재 필터 설정
 * @returns {JSX.Element}
 */
const KpiChart = ({
  kpiData,
  isLoading,
  onFilterChange,
  filter
}) => {
  const theme = useTheme();
  const { domain } = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // 현재 차트의 필터 상태
  const [localFilter, setLocalFilter] = useState({
    categoryCd: kpiData?.categoryCd,
    date: filter?.date || new Date().toISOString().split('T')[0],
    range: filter?.range || 'week'
  });
  
  // 날짜 객체로 변환
  const dateObject = useMemo(() => {
    try {
      if (!localFilter.date) return new Date();
      return parse(localFilter.date, 'yyyy-MM-dd', new Date());
    } catch (error) {
      console.error('날짜 파싱 오류:', error);
      return new Date();
    }
  }, [localFilter.date]);
  
  // 외부 필터가 변경되면 로컬 필터도 업데이트
  useEffect(() => {
    if (filter) {
      setLocalFilter({
        categoryCd: kpiData?.categoryCd,
        date: filter.date,
        range: filter.range
      });
    }
  }, [filter, kpiData?.categoryCd]);
  
  // 도메인별 색상 설정
  const getTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)';
    }
    return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)';
  };
  
  const getSecondaryTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? 'rgba(240, 230, 217, 0.7)' : 'rgba(0, 0, 0, 0.6)';
    }
    return isDarkMode ? 'rgba(179, 197, 230, 0.7)' : 'rgba(0, 0, 0, 0.6)';
  };
  
  const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  
  // 도메인별 차트 색상 - 그라디언트 및 고급 색상 (검정색 제거)
  const getChartColors = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode 
        ? [
            { solid: '#e67e22', gradient: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)' },
            { solid: '#f39c12', gradient: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' },
            { solid: '#3498db', gradient: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' },
            { solid: '#1abc9c', gradient: 'linear-gradient(135deg, #1abc9c 0%, #16a085 100%)' },
            { solid: '#9b59b6', gradient: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)' }
          ]
        : [
            { solid: '#d35400', gradient: 'linear-gradient(135deg, #d35400 0%, #e74c3c 100%)' },
            { solid: '#e74c3c', gradient: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)' },
            { solid: '#2980b9', gradient: 'linear-gradient(135deg, #2980b9 0%, #3498db 100%)' },
            { solid: '#16a085', gradient: 'linear-gradient(135deg, #16a085 0%, #1abc9c 100%)' },
            { solid: '#8e44ad', gradient: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)' }
          ];
    }
    return [
      { solid: '#667eea', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
      { solid: '#f093fb', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
      { solid: '#4facfe', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
      { solid: '#43e97b', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
      { solid: '#e67e22', gradient: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)' }
    ];
  };
  
  const getTooltipBgColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#3d2814' : '#ffffff';
    }
    return isDarkMode ? '#1e3a5f' : '#ffffff';
  };
  
  const getTooltipBorderColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
    }
    return isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
  };

  // 차트 데이터 키 추출
  const dataKeys = useMemo(() => {
    if (!kpiData || !kpiData.chartData || kpiData.chartData.length === 0) {
      return [];
    }

    // 첫 번째 데이터 포인트에서 "name", "isHoliday" 키를 제외한 모든 키 추출
    const firstDataPoint = kpiData.chartData[0];
    return Object.keys(firstDataPoint).filter(key => key !== 'name' && key !== 'isHoliday' && !key.startsWith('__') && !key.startsWith('forecast_'));
  }, [kpiData]);

  // Y축 도메인 계산 (사업이전값과 목표값을 포함하도록)
  const yAxisDomain = useMemo(() => {
    if (!kpiData || !kpiData.chartData || kpiData.chartData.length === 0) {
      return ['auto', 'auto'];
    }

    // 차트 데이터의 모든 값 수집
    const allValues = [];
    kpiData.chartData.forEach(point => {
      dataKeys.forEach(key => {
        const value = point[key];
        if (typeof value === 'number' && !isNaN(value)) {
          allValues.push(value);
        }
      });
    });

    // 목표값과 사업이전값 추가
    if (kpiData.targetValue && typeof kpiData.targetValue === 'number') {
      allValues.push(kpiData.targetValue);
    }
    if (kpiData.currentValue && typeof kpiData.currentValue === 'number') {
      allValues.push(kpiData.currentValue);
    }

    if (allValues.length === 0) {
      return ['auto', 'auto'];
    }

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    // 여유 공간 추가 (10%)
    const padding = (maxValue - minValue) * 0.1;
    return [Math.max(0, minValue - padding), maxValue + padding];
  }, [kpiData, dataKeys]);
  
  // 범위 변경 핸들러
  const handleRangeChange = (e) => {
    const { value } = e.target;
    const newFilter = {
      ...localFilter,
      categoryCd: kpiData.categoryCd,
      range: value
    };
    
    setLocalFilter(newFilter);
    
    // 부모 컴포넌트에 필터 변경 알림 (즉시 데이터 요청)
    if (onFilterChange) {
      onFilterChange(kpiData.kpiIndicatorCd, newFilter, true);
    }
  };
  
  // 날짜 변경 핸들러
  const handleDateChange = (newDate) => {
    if (!newDate) return;
    
    try {
      const formattedDate = format(newDate, 'yyyy-MM-dd');
      const newFilter = {
        ...localFilter,
        categoryCd: kpiData.categoryCd,
        date: formattedDate
      };
      
      setLocalFilter(newFilter);
      
      // 부모 컴포넌트에 필터 변경 알림 (즉시 데이터 요청)
      if (onFilterChange) {
        onFilterChange(kpiData.kpiIndicatorCd, newFilter, true);
      }
    } catch (error) {
      console.error('날짜 변환 오류:', error);
    }
  };
  
  // 필터 새로고침 핸들러
  const handleRefresh = () => {
    if (onFilterChange) {
      const refreshFilter = {
        ...localFilter,
        categoryCd: kpiData.categoryCd
      };
      onFilterChange(kpiData.kpiIndicatorCd, refreshFilter, true);
    }
  };

  // 목표 / 사업이전값 칩 렌더링 함수
  const renderReferenceChip = (value, label, color, xOffset = -10) => {
    if (!value) return null;

    return (
      <ReferenceLine
        y={value}
        stroke="transparent"
        label={(props) => {
          const { viewBox } = props;
          if (!viewBox) return null;
          const text = `${label}: ${Number(value).toFixed(0)}${kpiData.unit || ''}`;
          const textWidth = text.length * 8;
          const rectWidth = Math.max(textWidth + 16, label.includes('사업이전값') ? 80 : 60);

          return (
            <g>
              <rect
                x={viewBox.width - rectWidth + xOffset}
                y={viewBox.y - 12}
                width={rectWidth}
                height="24"
                fill={color}
                rx="12"
                ry="12"
                opacity="0.9"
              />
              <text
                x={viewBox.width + xOffset - rectWidth/2}
                y={viewBox.y + 4}
                textAnchor="middle"
                fill="white"
                fontSize="12"
                fontWeight="700"
              >
                {text}
              </text>
            </g>
          );
        }}
      />
    );
  };

  // 차트 타입에 따른 컴포넌트 렌더링
  // TODO: 예측치 관련 로직은 추후 삭제 예정 (임시 8PIN 조건부 처리)
  const renderChart = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!kpiData || !kpiData.chartData || kpiData.chartData.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body1" color={getSecondaryTextColor()}>
            데이터가 없습니다.
          </Typography>
        </Box>
      );
    }

    // TODO: 임시 8PIN 조건 - 추후 삭제 예정
    const is8PinCondition = kpiData?.kpiTitle?.includes('에잇핀') ||
                           dataKeys.some(key => key?.includes('에잇핀'));
    
    const chartColors = getChartColors();
    const actualKeys = dataKeys;
    const forecastSeriesMeta = kpiData?.forecastMeta?.series || {};
    const forecastEntries = actualKeys
      .map((key) => {
        const meta = forecastSeriesMeta[key];
        const forecastKey = meta?.forecastKey || `forecast_${key}`;
        const hasForecastValue = Array.isArray(kpiData.chartData) && kpiData.chartData.some(point => typeof point?.[forecastKey] === 'number');
        if (!hasForecastValue) return null;

        let detectedStartIndex = meta?.startIndex;
        if (detectedStartIndex == null) {
          const index = kpiData.chartData.findIndex((point, idx) => {
            const forecastVal = point?.[forecastKey];
            const hasActual = typeof point?.[key] === 'number';
            return typeof forecastVal === 'number' && !hasActual && idx > 0;
          });
          detectedStartIndex = index === -1 ? null : index;
        }

        const lastActualIndex = meta?.lastActualIndex ?? (detectedStartIndex != null ? detectedStartIndex - 1 : kpiData.chartData.length - 1);

        return {
          metricKey: key,
          forecastKey,
          startIndex: detectedStartIndex,
          lastActualIndex
        };
      })
      .filter(Boolean);

    const forecastInfoByKey = forecastEntries.reduce((acc, entry) => {
      acc[entry.metricKey] = entry;
      return acc;
    }, {});

    const hasForecast = forecastEntries.length > 0;
    const earliestStartIndex = hasForecast
      ? forecastEntries.reduce((min, item) => {
          const idx = item.startIndex ?? (item.lastActualIndex != null ? item.lastActualIndex + 1 : null);
          if (idx == null) return min;
          return min == null ? idx : Math.min(min, idx);
        }, null)
      : null;
    const forecastStartLabel = earliestStartIndex != null && earliestStartIndex > -1
      ? kpiData.chartData[earliestStartIndex]?.name
      : null;
    const forecastEndLabel = hasForecast && kpiData.chartData.length > 0
      ? kpiData.chartData[kpiData.chartData.length - 1]?.name
      : null;
    const lastActualIndexGlobal = hasForecast
      ? forecastEntries.reduce((max, entry) => {
          if (entry.lastActualIndex == null) return max;
          return max == null ? entry.lastActualIndex : Math.max(max, entry.lastActualIndex);
        }, null)
      : null;
    const lastActualLabel = lastActualIndexGlobal != null && lastActualIndexGlobal > -1
      ? kpiData.chartData[lastActualIndexGlobal]?.name
      : null;
    const shadingColor = hasForecast
      ? lightenHexColor(chartColors[Math.max(0, actualKeys.indexOf(forecastEntries[0]?.metricKey)) % chartColors.length].solid, 0.5)
      : null;

    const formatTooltipValue = (value, name, props) => {
      const dataKey = props?.dataKey;
      const isForecastKey = typeof dataKey === 'string' && dataKey.startsWith('forecast_');
      const displayName = name || (isForecastKey ? dataKey.replace('forecast_', '') : dataKey);

      if (value == null || value === '') {
        return ['-', displayName];
      }
      const numericValue = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(numericValue)) {
        return [value, displayName];
      }
      const unitLabel = kpiData.unit ? ` ${kpiData.unit}` : '';
      return [`${numericValue.toFixed(2)}${unitLabel}`, displayName];
    };

    const normalizedChartData = Array.isArray(kpiData.chartData)
      ? kpiData.chartData.map((entry) => {
          const normalized = { ...entry };
          actualKeys.forEach((key) => {
            const value = normalized[key];
            if (value == null || value === '') {
              normalized[key] = null;
            } else {
              const numericValue = Number(value);
              normalized[key] = Number.isFinite(numericValue) ? numericValue : null;
            }
          });
          forecastEntries.forEach(({ forecastKey }) => {
            const value = normalized[forecastKey];
            if (value == null || value === '') {
              normalized[forecastKey] = null;
            } else {
              const numericValue = Number(value);
              normalized[forecastKey] = Number.isFinite(numericValue) ? numericValue : null;
            }
          });
          return normalized;
        })
      : [];

    
    // targetType에 따른 달성 여부 확인 및 색상 결정 함수
    const getBarColorForValue = (currentValue) => {
      const targetValue = kpiData.targetValue;
      const targetType = kpiData.targetType;
      
      if (!targetValue || currentValue === null || currentValue === undefined) {
        return chartColors[0]; // 기본 색상
      }
      
      let isAchieved = false;
      switch (targetType?.toUpperCase()) {
        case 'INCREASE':
          isAchieved = currentValue >= targetValue;
          break;
        case 'DECREASE':
          isAchieved = currentValue <= targetValue;
          break;
        case 'EQUAL':
          const tolerance = targetValue * 0.05; // 5% 허용 오차
          isAchieved = Math.abs(currentValue - targetValue) <= tolerance;
          break;
        default:
          isAchieved = currentValue >= targetValue; // 기본: INCREASE
      }
      
      return isAchieved ? '#4caf50' : '#f44336'; // 달성: 녹색, 미달성: 빨간색
    };
    
    // 현재 필터 범위에 따른 X축 라벨 포맷 설정
    const formatXAxis = (value, index) => {
      if (!value) return '';

      let formattedLabel = value;

      // 날짜 형식(YYYY-MM-DD)인지 확인
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        switch (localFilter.range) {
          case 'day':
            formattedLabel = value.substring(5); // MM-DD 형식으로 표시
            break;
          case 'week':
            formattedLabel = value.substring(5); // MM-DD 형식으로 표시
            break;
          case 'month':
            // M/DD 형식으로 표시 (앞의 0 제거)
            const month = parseInt(value.substring(5, 7), 10);
            const day = value.substring(8, 10);
            formattedLabel = `${month}/${day}`;
            break;
          default:
            formattedLabel = value;
        }
      }
      // 시간 형식인 경우 (08, 09, 10 등)
      else if (localFilter.range === 'day' && /^\d{1,2}$/.test(value)) {
        formattedLabel = `${value}시`;
      }

      // 공휴일 표시 추가
      if (index !== undefined && normalizedChartData[index]?.isHoliday) {
        formattedLabel += ' 🔴️';
      }

      return formattedLabel;
    };
    
    // 차트 타입에 따라 다른 차트 렌더링
    switch (kpiData.chartType?.toLowerCase()) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={normalizedChartData}
              margin={{ top: 20, right: 20, left: 15, bottom: 15 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="name"
                tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                tickFormatter={formatXAxis}
                axisLine={{ stroke: getSecondaryTextColor() }}
                tickLine={{ stroke: getSecondaryTextColor() }}
                angle={-45}
                textAnchor="end"
                height={40}
              />
              <YAxis
                domain={yAxisDomain}
                tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                tickFormatter={(value) => {
                  if (typeof value === 'number') {
                    if (value >= 1000000) {
                      return `${(value / 1000000).toFixed(1)}M`;
                    } else if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                  }
                  return value;
                }}
                axisLine={{ stroke: getSecondaryTextColor() }}
                tickLine={{ stroke: getSecondaryTextColor() }}
                label={{
                  value: kpiData.unit,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: getTextColor(), fontSize: 12, fontWeight: 600 }
                }}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(12px)',
                  color: '#2d3748',
                  borderRadius: 12,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15), 0 0 0 1px rgba(99, 102, 241, 0.1)',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: 600
                }}
                formatter={(value, name) => {
                  const unitLabel = kpiData.unit ? ` ${kpiData.unit}` : '';
                  return [`${value}${unitLabel}`, name];
                }}
                labelFormatter={formatXAxis}
              />
              <Legend wrapperStyle={{
                color: getTextColor(),
                fontSize: '14px',
                fontWeight: 700
              }} />
              {/* 점선들 - 가장 먼저 렌더링 (뒤에 배치) */}
              {kpiData.targetValue && (
                <ReferenceLine
                  y={kpiData.targetValue}
                  stroke="#ff0000"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  strokeOpacity={0.8}
                />
              )}
              {kpiData.currentValue && (
                <ReferenceLine
                  y={kpiData.currentValue}
                  stroke="#2196f3"
                  strokeDasharray="8 4"
                  strokeWidth={2}
                  strokeOpacity={0.8}
                />
              )}
              {dataKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={key}
                  radius={[4, 4, 0, 0]}
                  strokeWidth={2}
                >
                  {normalizedChartData.map((entry, entryIndex) => {
                    const currentValue = entry[key];
                    const targetValue = kpiData.targetValue;
                    const targetType = kpiData.targetType;

                    let barColor = chartColors[index % chartColors.length].solid; // 기본 색상

                    if (targetValue && currentValue !== null && currentValue !== undefined) {
                      let isAchieved = false;
                      switch (targetType?.toUpperCase()) {
                        case 'INCREASE':
                          isAchieved = currentValue >= targetValue;
                          break;
                        case 'DECREASE':
                          isAchieved = currentValue <= targetValue;
                          break;
                        case 'EQUAL':
                          const tolerance = targetValue * 0.05; // 5% 허용 오차
                          isAchieved = Math.abs(currentValue - targetValue) <= tolerance;
                          break;
                        default:
                          isAchieved = currentValue >= targetValue; // 기본: INCREASE
                      }

                      barColor = isAchieved ? '#23cd5c' : '#f5576c'; // 달성: 모던 그린, 미달성: 모던 레드
                    }

                    return (
                      <Cell
                        key={`cell-${entryIndex}`}
                        fill={barColor}
                        stroke={barColor}
                        style={{
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }}
                      />
                    );
                  })}
                </Bar>
              ))}
              {/* 예측 데이터 막대들 - TODO: 8PIN 조건부 처리, 추후 삭제 예정 */}
              {is8PinCondition && forecastEntries.map(({ forecastKey, metricKey }, index) => {
                const color = chartColors[Math.max(0, dataKeys.indexOf(metricKey)) % chartColors.length].solid;
                const forecastColor = lightenHexColor(color, 0.4);
                return (
                  <Bar
                    key={forecastKey}
                    dataKey={forecastKey}
                    name={`${metricKey} (예상)`}
                    fill={forecastColor}
                    radius={[4, 4, 0, 0]}
                    strokeWidth={1}
                    fillOpacity={0.7}
                  />
                );
              })}
              {/* 라벨 칩들 - 최상위에 표시 */}
              {renderReferenceChip(kpiData.targetValue, '목표', '#ff0000')}
              {renderReferenceChip(kpiData.currentValue, '사업이전값', '#2196f3', -30)}
            </BarChart>
          </ResponsiveContainer>
        );
        
      case 'line':
      default:
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={normalizedChartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis 
                dataKey="name"
                tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                tickFormatter={formatXAxis}
                axisLine={{ stroke: getSecondaryTextColor() }}
                tickLine={{ stroke: getSecondaryTextColor() }}
                angle={-45}
                textAnchor="end"
                height={40}
              />
              <YAxis
                domain={yAxisDomain}
                tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                axisLine={{ stroke: getSecondaryTextColor() }}
                tickLine={{ stroke: getSecondaryTextColor() }}
                tickFormatter={(value) => {
                  if (typeof value === 'number') {
                    if (value >= 1000000) {
                      return `${(value / 1000000).toFixed(1)}M`;
                    } else if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                  }
                  return Number(value).toFixed(2);
                }}
                label={{
                  value: kpiData.unit,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: getTextColor(), fontSize: 12, fontWeight: 600 }
                }}
              />
              <RechartsTooltip 
                contentStyle={{ 
                  backgroundColor: getTooltipBgColor(),
                  borderColor: getTooltipBorderColor(),
                  color: getTextColor(),
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: 600
                }}
                formatter={formatTooltipValue}
                labelFormatter={formatXAxis}
              />
              <Legend wrapperStyle={{ color: getTextColor(), fontSize: '16px', fontWeight: 600 }} />
              {kpiData.targetValue && (
                <ReferenceLine
                  y={kpiData.targetValue}
                  stroke="#ff0000"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                />
              )}
              {kpiData.currentValue && (
                <ReferenceLine
                  y={kpiData.currentValue}
                  stroke="#2196f3"
                  strokeDasharray="8 4"
                  strokeWidth={2}
                />
              )}
              {/* TODO: 8PIN 조건부 예측 영역 - 추후 삭제 예정 */}
              {is8PinCondition && hasForecast && forecastStartLabel && forecastEndLabel && (
                <ReferenceArea
                  x1={lastActualLabel || forecastStartLabel}
                  x2={forecastEndLabel}
                  strokeOpacity={0}
                  fill={shadingColor || '#90caf9'}
                  fillOpacity={0.12}
                />
              )}
              {actualKeys.map((key, index) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={chartColors[index % chartColors.length].solid}
                  strokeWidth={4}
                  dot={{
                    r: 4,
                    fill: chartColors[index % chartColors.length].solid,
                    stroke: '#ffffff',
                    strokeWidth: 2
                  }}
                  activeDot={{
                    r: 8,
                    fill: chartColors[index % chartColors.length].solid,
                    stroke: '#ffffff',
                    strokeWidth: 3,
                    style: {
                      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                    }
                  }}
                  name={key}
                  style={{
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                  }}
                />
              ))}
              {/* TODO: 8PIN 조건부 예측 라인 - 추후 삭제 예정 */}
              {is8PinCondition && forecastEntries.map(({ metricKey, forecastKey, startIndex }) => {
                const color = chartColors[Math.max(0, actualKeys.indexOf(metricKey)) % chartColors.length].solid;
                const forecastStroke = lightenHexColor(color, 0.45);
                const forecastInfo = forecastInfoByKey[metricKey];
                const effectiveStartIndex = startIndex ?? (forecastInfo?.lastActualIndex != null ? forecastInfo.lastActualIndex + 1 : null);
                return (
                  <Line
                    key={forecastKey}
                    type="monotone"
                    dataKey={forecastKey}
                    stroke={forecastStroke}
                    strokeWidth={3}
                    strokeDasharray="6 6"
                    connectNulls
                    dot={(props) => {
                      if (!props || !props.payload) {
                        return null;
                      }
                      const pointIndex = props.index ?? kpiData.chartData.indexOf(props.payload);
                      const hasActualValue = typeof props.payload?.[metricKey] === 'number';
                      const isForecastValue = typeof props.payload?.[forecastKey] === 'number';
                      const isFuturePoint = effectiveStartIndex == null
                        ? isForecastValue && !hasActualValue
                        : pointIndex >= effectiveStartIndex;
                      if (!isForecastValue || hasActualValue || !isFuturePoint) {
                        return null;
                      }
                      return (
                        <circle
                          key={`forecast-dot-${metricKey}-${pointIndex}`}
                          cx={props.cx}
                          cy={props.cy}
                          r={5}
                          fill={forecastStroke}
                          stroke="#ffffff"
                          strokeWidth={2}
                        />
                      );
                    }}
                    activeDot={{
                      r: 7,
                      fill: '#ffffff',
                      stroke: forecastStroke,
                      strokeWidth: 3,
                      style: {
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.25))'
                      }
                    }}
                    name={`${metricKey} (예상)`}
                  />
                );
              })}
              {/* 라벨 칩들 - 최상위에 표시 */}
              {renderReferenceChip(kpiData.targetValue, '목표', '#ff0000')}
              {renderReferenceChip(kpiData.currentValue, '사업이전값', '#2196f3', -100)}
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <Box sx={{ 
      p: 0.5,
      color: getTextColor(),
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <DashboardTitle
            icon={AssessmentIcon}
            title={kpiData?.kpiTitle || 'KPI 지표'}
            subtitle=""
            size="small"
          />
          {kpiData?.categoryNm && (
            <Chip
              label={kpiData.categoryNm}
              size="medium"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                height: { xs: '24px', sm: '28px' },
                '& .MuiChip-label': {
                  px: { xs: 1.5, sm: 2 }
                },
                boxShadow: '0 3px 12px rgba(102, 126, 234, 0.4)',
                border: 'none',
                display: { xs: kpiData.categoryNm.length > 10 ? 'none' : 'inline-flex', sm: 'inline-flex' }
              }}
            />
          )}
          <Tooltip title="공휴일" arrow placement="top">
            <Chip
              label="🔴 공휴일"
              size="small"
              sx={{
                bgcolor: 'rgba(244, 67, 54, 0.1)',
                color: '#f44336',
                fontWeight: 500,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                height: { xs: '20px', sm: '24px' },
                '& .MuiChip-label': {
                  px: { xs: 1, sm: 1.5 }
                },
                border: '1px solid rgba(244, 67, 54, 0.3)',
                cursor: 'help'
              }}
            />
          </Tooltip>
        </Box>
        
        {/* 반응형 필터 컨트롤 */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1 },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          background: 'rgba(248, 250, 252, 0.8)',
          borderRadius: 2,
          p: { xs: 0.75, sm: 1 },
          border: '2px solid #e2e8f0',
          justifyContent: { xs: 'flex-end', md: 'flex-start' }
        }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <DatePicker
              label="조회일자"
              value={dateObject}
              onChange={handleDateChange}
              slotProps={{ 
                textField: { 
                  size: "small",
                  sx: {
                    width: { xs: '120px', sm: '140px', md: '170px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      background: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.95)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea'
                        }
                      },
                      '&.Mui-focused': {
                        background: '#ffffff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea',
                          borderWidth: '2px'
                        }
                      }
                    }
                  }
                } 
              }}
            />
          </LocalizationProvider>
          
          <FormControl 
            size="small" 
            sx={{ 
              minWidth: { xs: 65, sm: 75, md: 90 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                transition: 'all 0.3s ease',
                background: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.95)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea'
                  }
                },
                '&.Mui-focused': {
                  background: '#ffffff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                    borderWidth: '2px'
                  }
                }
              }
            }}
          >
            <Select
              name="range"
              value={localFilter.range}
              onChange={handleRangeChange}
              sx={{ 
                color: getTextColor(),
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                fontWeight: 500
              }}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '& .MuiMenuItem-root': {
                      borderRadius: 1,
                      mx: 0.5,
                      my: 0.25,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#ffffff'
                      },
                      '&.Mui-selected': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#ffffff',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                        }
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value="day">일간</MenuItem>
              <MenuItem value="week">주간</MenuItem>
              <MenuItem value="month">월간</MenuItem>
            </Select>
          </FormControl>
          
          <IconButton 
            size="small" 
            onClick={handleRefresh}
            sx={{ 
              p: { xs: 0.75, sm: 1 },
              color: '#667eea',
              transition: 'all 0.3s ease',
              '&:hover': {
                color: '#5a6fd8',
                background: 'rgba(102, 126, 234, 0.1)',
                transform: 'translateY(-1px)'
              }
            }}
          >
            <RefreshIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
          </IconButton>
        </Box>
      </Box>
      
      <Box sx={{ 
        flex: 1, 
        width: '100%', 
        minHeight: { xs: 120, md: 140 },
        height: '100%',
        position: 'relative'
      }}>
        {renderChart()}
      </Box>
    </Box>
  );
};

export default KpiChart; 
